import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  AlertTitle,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import apiClient from '../../../config/axios';
import ModalConfirm from '../../../components/ModalConfirm';
import ModalResult from '../../../components/ModalResult';
import ModalArchivePreview from './ModalArchivePreview';

dayjs.locale('it');

const STATUS_CHIP = {
  in_progress: { label: 'Incompleto', color: 'error' },
  active: { label: 'Archiviato', color: 'primary' },
  restored: { label: 'Ripristinato', color: 'default' }
};

const formatDate = (value) => (value ? dayjs(value).format('DD MMM YYYY') : '-');

const totalDocuments = (counts = {}) => Object.values(counts).reduce((sum, count) => sum + (count || 0), 0);

const defaultLabel = () => {
  const now = dayjs();
  // L'anno sociale parte a settembre: prima di quel mese siamo ancora nell'anno precedente
  const startYear = now.month() >= 8 ? now.year() : now.year() - 1;
  return `Anno sociale ${startYear}/${startYear + 1}`;
};

const AdvancedPage = () => {
  const [label, setLabel] = useState(defaultLabel);
  const [notes, setNotes] = useState('');
  const [cutoffDate, setCutoffDate] = useState(dayjs());

  const [archives, setArchives] = useState([]);
  const [loadingArchives, setLoadingArchives] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [confirmAbort, setConfirmAbort] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [resultOpen, setResultOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [resultSeverity, setResultSeverity] = useState('info');

  const showResult = (message, severity) => {
    setResultMessage(message);
    setResultSeverity(severity);
    setResultOpen(true);
  };

  const readError = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || fallback;

  const fetchArchives = useCallback(async () => {
    try {
      setLoadingArchives(true);
      const response = await apiClient.get('/api/season-archive');
      setArchives(response.data.data || []);
      setError(null);
    } catch (err) {
      setError(readError(err, 'Errore nel caricamento degli archivi'));
    } finally {
      setLoadingArchives(false);
    }
  }, []);

  useEffect(() => {
    fetchArchives();
  }, [fetchArchives]);

  const handleOpenPreview = async () => {
    setPreview(null);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const response = await apiClient.get('/api/season-archive/preview', {
        params: { cutoffDate: cutoffDate.toISOString() }
      });
      setPreview(response.data.data);
    } catch (err) {
      setPreviewOpen(false);
      showResult(readError(err, "Errore nel calcolo dell'anteprima"), 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleArchive = async () => {
    setConfirmArchiveOpen(false);
    setPreviewOpen(false);
    setBusy(true);
    try {
      const response = await apiClient.post('/api/season-archive', {
        label: label.trim(),
        cutoffDate: cutoffDate.toISOString(),
        notes: notes.trim() || undefined
      });
      showResult(`Archivio "${response.data.data.label}" creato con successo.`, 'success');
      setNotes('');
      await fetchArchives();
    } catch (err) {
      const archiveId = err?.response?.data?.archiveId;
      showResult(
        readError(err, "Errore durante l'archiviazione") +
          (archiveId ? " L'archiviazione e' rimasta incompleta: annullala dalla tabella qui sotto." : ''),
        'error'
      );
      await fetchArchives();
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    const archive = confirmRestore;
    setConfirmRestore(null);
    if (!archive) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/season-archive/${archive._id}/restore`);
      showResult(`Archivio "${archive.label}" ripristinato: i dati sono di nuovo attivi.`, 'success');
      await fetchArchives();
    } catch (err) {
      showResult(readError(err, 'Errore durante il ripristino'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleAbort = async () => {
    const archive = confirmAbort;
    setConfirmAbort(null);
    if (!archive) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/season-archive/${archive._id}/abort`);
      showResult(`Archiviazione "${archive.label}" annullata: nessun dato risulta archiviato.`, 'success');
      await fetchArchives();
    } catch (err) {
      showResult(readError(err, "Errore durante l'annullamento"), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    const archive = confirmDelete;
    setConfirmDelete(null);
    if (!archive) return;
    setBusy(true);
    try {
      await apiClient.delete(`/api/season-archive/${archive._id}`);
      showResult(`Archivio "${archive.label}" eliminato: l'etichetta e' di nuovo utilizzabile.`, 'success');
      await fetchArchives();
    } catch (err) {
      showResult(readError(err, "Errore durante l'eliminazione"), 'error');
      await fetchArchives();
    } finally {
      setBusy(false);
    }
  };

  const canPreview = Boolean(label.trim()) && cutoffDate?.isValid?.() && !busy;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
      <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Typography variant="h5" gutterBottom>
          Funzioni Avanzate
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <ArchiveOutlinedIcon color="warning" />
            <Typography variant="h6">Archivia Anno Sociale</Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Chiude i corsi conclusi e mette da parte iscrizioni, pagamenti, tesseramenti, spese e movimenti con i docenti
            fino alla data di taglio. I dati non vengono cancellati: restano consultabili in sola lettura dalla Gestione
            Finanziaria e l'ultimo archivio puo' essere ripristinato.
          </Typography>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Prima di procedere</AlertTitle>
            Esegui un backup del database. L'operazione tocca l'intero storico contabile dell'associazione.
          </Alert>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Etichetta archivio"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              fullWidth
              size="small"
              required
            />
            <DatePicker
              label="Data di taglio"
              value={cutoffDate}
              onChange={(value) => setCutoffDate(value)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Stack>

          <TextField
            label="Note (facoltative)"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />

          <Button variant="contained" color="warning" onClick={handleOpenPreview} disabled={!canPreview}>
            Calcola anteprima
          </Button>
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" gutterBottom>
            Archivi
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loadingArchives && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!loadingArchives && archives.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nessun anno sociale archiviato finora.
            </Typography>
          )}

          {!loadingArchives && archives.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Etichetta</TableCell>
                    <TableCell>Periodo</TableCell>
                    <TableCell align="right">Documenti</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell>Stato</TableCell>
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {archives.map((archive) => {
                    const chip = STATUS_CHIP[archive.status] || { label: archive.status, color: 'default' };
                    return (
                      <TableRow key={archive._id}>
                        <TableCell>
                          <Typography variant="body2">{archive.label}</Typography>
                          {archive.notes && (
                            <Typography variant="caption" color="text.secondary">
                              {archive.notes}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDate(archive.periodFrom)} — {formatDate(archive.periodTo)}
                        </TableCell>
                        <TableCell align="right">{totalDocuments(archive.counts)}</TableCell>
                        <TableCell align="right">{Number(archive.totals?.net || 0).toFixed(2)}€</TableCell>
                        <TableCell>
                          <Chip size="small" label={chip.label} color={chip.color} />
                        </TableCell>
                        <TableCell align="right">
                          {archive.status === 'in_progress' && (
                            <Button size="small" color="error" disabled={busy} onClick={() => setConfirmAbort(archive)}>
                              Annulla archiviazione
                            </Button>
                          )}
                          {archive.status === 'active' && archive.isRestorable && (
                            <Button
                              size="small"
                              color="warning"
                              startIcon={<RestoreIcon />}
                              disabled={busy}
                              onClick={() => setConfirmRestore(archive)}
                            >
                              Ripristina
                            </Button>
                          )}
                          {archive.status === 'active' && !archive.isRestorable && (
                            <Typography variant="caption" color="text.secondary">
                              Ripristinabile solo l'ultimo
                            </Typography>
                          )}
                          {archive.status === 'restored' && (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteOutlineIcon />}
                              disabled={busy}
                              onClick={() => setConfirmDelete(archive)}
                            >
                              Elimina
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <ModalArchivePreview
          open={previewOpen}
          loading={previewLoading}
          preview={preview}
          label={label}
          onArchive={() => setConfirmArchiveOpen(true)}
          onCancel={() => setPreviewOpen(false)}
        />

        <ModalConfirm
          open={confirmArchiveOpen}
          title="Archivia Anno Sociale"
          message={`Stai per archiviare ${totalDocuments(preview?.counts)} documenti sotto l'etichetta "${label}". I corsi conclusi verranno chiusi e i dati spariranno dalle viste correnti. Confermi?`}
          onConfirm={handleArchive}
          onCancel={() => setConfirmArchiveOpen(false)}
        />

        <ModalConfirm
          open={Boolean(confirmRestore)}
          title="Ripristina Archivio"
          message={`Stai per riportare in vita l'archivio "${confirmRestore?.label}". I corsi chiusi torneranno pubblicati e tutti i movimenti rientreranno nei bilanci correnti. Confermi?`}
          onConfirm={handleRestore}
          onCancel={() => setConfirmRestore(null)}
        />

        <ModalConfirm
          open={Boolean(confirmAbort)}
          title="Annulla Archiviazione Incompleta"
          message={`L'archiviazione "${confirmAbort?.label}" non e' andata a buon fine. Annullandola i documenti gia' archiviati tornano attivi e l'archivio viene eliminato. Confermi?`}
          onConfirm={handleAbort}
          onCancel={() => setConfirmAbort(null)}
        />

        <ModalConfirm
          open={Boolean(confirmDelete)}
          title="Elimina Archivio"
          message={`Stai per eliminare la registrazione dell'archivio "${confirmDelete?.label}". I dati sono già stati ripristinati e restano al loro posto: viene rimossa solo la riga dallo storico e l'etichetta torna riutilizzabile. Confermi?`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />

        <ModalResult
          open={resultOpen}
          onClose={() => setResultOpen(false)}
          message={resultMessage}
          severity={resultSeverity}
          title={resultSeverity === 'success' ? 'Successo' : 'Errore'}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default AdvancedPage;
